// Styles must use direct files imports
import 'swiper/scss/navigation'; // Navigation module
import 'swiper/scss/pagination'; // Pagination module
import 'swiper/scss'; // core Swiper
import './Slider.scss'; // b2x customization

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';
import React from 'react';
// Core modules imports are same as usual
import SwiperInterface, { Autoplay, Navigation, Pagination } from 'swiper';
// Direct React component imports
import { Swiper } from 'swiper/react';
import { AutoplayOptions } from 'swiper/types';

export interface SliderProps {
  autoHeight?: boolean;
  autoplay?: AutoplayOptions;
  centeredSlides?: boolean;
  children?: React.ReactNode;
  className?: string;
  dir?: string;
  loop?: boolean;
  navigation?: boolean;
  onAfterInit?(swiper: SwiperInterface): void;
  onSwiper?(swiper: SwiperInterface): void;
  pagination?: boolean;
  responsive?: Partial<Record<SliderBreakpoint, SliderResponsiveProps>>;
  slideContentCentered?: boolean;
  sliderAutoPlay?: number;
  sliderNextEl?: React.RefObject<HTMLButtonElement> | string;
  sliderPrevEl?: React.RefObject<HTMLButtonElement> | string;
  slidesPerView: number | 'auto';
  spaceBetween?: number;
  speed?: number;
  vertical?: boolean;
  watchSlidesProgress?: boolean;
}
export type SliderBreakpoint = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type SliderResponsiveProps = Pick<SliderProps, 'slidesPerView'>;

export const Slider = ({
  autoHeight,
  autoplay,
  centeredSlides,
  children,
  className,
  dir,
  loop,
  navigation,
  onAfterInit,
  onSwiper,
  pagination,
  responsive,
  slideContentCentered,
  sliderNextEl,
  sliderPrevEl,
  slidesPerView,
  spaceBetween,
  speed,
  vertical,
  watchSlidesProgress,
}: SliderProps): React.ReactElement => {
  const breakpoint = b2x.useBreakpoint();

  return (
    <Swiper
      autoHeight={autoHeight ? true : false}
      autoplay={autoplay ? autoplay : undefined}
      centeredSlides={centeredSlides ? true : false}
      className={classnames(
        className,
        slidesPerView === 'auto' && 'auto-width',
        slideContentCentered && 'slide-content-centered'
      )}
      /* breakpoints sembra essere buggato, se da browser fai ingrandisci/rimpicciolisci la finestra varie volte
         provocando un cambio di breakpoint, alla lunga si rallenta tutto.
      */
      // breakpoints={}
      dir={dir}
      direction={vertical ? 'vertical' : 'horizontal'}
      loop={loop ? true : false}
      modules={[Pagination, Navigation, Autoplay]}
      navigation={
        navigation
          ? sliderNextEl !== undefined && sliderPrevEl !== undefined
            ? {
                nextEl: typeof sliderNextEl === 'string' ? sliderNextEl : sliderNextEl.current,
                prevEl: typeof sliderPrevEl === 'string' ? sliderPrevEl : sliderPrevEl.current,
              }
            : true
          : false
      }
      onAfterInit={onAfterInit}
      onSwiper={onSwiper}
      pagination={pagination ? { clickable: true } : false}
      slidesPerView={slidesPerView}
      spaceBetween={spaceBetween ? spaceBetween : 0}
      speed={speed ? speed : undefined}
      watchSlidesProgress={watchSlidesProgress}
      {...(breakpoint === 'sm' && { ...responsive?.sm })}
      {...(breakpoint === 'md' && { ...responsive?.sm, ...responsive?.md })}
      {...(breakpoint === 'lg' && { ...responsive?.sm, ...responsive?.md, ...responsive?.lg })}
      {...(breakpoint === 'xl' && {
        ...responsive?.sm,
        ...responsive?.md,
        ...responsive?.lg,
        ...responsive?.xl,
      })}
      {...(breakpoint === 'xxl' && {
        ...responsive?.sm,
        ...responsive?.md,
        ...responsive?.lg,
        ...responsive?.xl,
        ...responsive?.xxl,
      })}
    >
      {children}
    </Swiper>
  );
};
